import { Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { ThemeProvider } from "@emotion/react";
import { CssBaseline } from "@mui/material";
import { ReactQueryDevtools } from "react-query/devtools";
import { SnackbarProvider } from "notistack";

import Home from "./component/pages/Home";
import CleaningRobotList from "./component/pages/CleaningRobotList";
import ScheduleList from "./component/pages/ScheduleList";
import { RobotDetailPage } from "./component/pages/RobotDetail";
import CleaningLog from "./component/pages/CleaningLog/CleaningLog";
import MapCleaningLog from "./component/pages/CleaningLog/MapCleaningLog";
import theme from "./style/theme";
import { CleaningSchedulePage } from "./component/pages/CleaningSchedule";
import SelectionWork from "./component/pages/SelectionWork";
import MapSchedule from "./component/pages/MapSchedule/MapSchedule";
import { RobotListPage } from "./component/pages/RobotList";
import MapDetail from "./component/pages/MapDetail";
import {
  pathToSelectionWork,
  pathToCleaningEvent,
  pathToSchedule,
  pathToRobotStatus,
  pathToMapSchedule,
  pathToBuildingList,
  baseRobotDetailPath,
  baseCleaningLogPath,
  baseMapDetailPath,
  baseCleaningSchedule,
  baseMapCleaningLogPath,
} from "./helpers/paths";
import StackSnackbarManager from "./component/Snackbar/StackSnackbarManager";
import { APP_ROUTES } from "./routes/appRoutes";
import AuthGuard from "./routes/AuthGuard";
import UnAuthGuard from "./routes/UnAuthGuard";
import NotFoundPage from "./component/Error/NotFoundPage";
import RoleGuard from "./routes/RoleGuard";
import { AppUserRole } from "./types/roles";
import FullPageLoader from "./component/common/FullPageLoader";
import { ErrorBoundary } from "./component/Error/ErrorBounndary";
import WorkerGurad from "routes/WorkerGuard";
import ClearCacheOnRouteChange from "component/cache/ClearCacheOnRouteChange";

const awsconfig = {
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_LOGIN_POOL_ID ?? "",
      userPoolClientId: process.env.REACT_APP_LOGIN_CLIENT_ID ?? "",
      region: process.env.REACT_APP_AWS_REGION ?? "",
    },
  },
};

Amplify.configure(awsconfig);

export default function App() {
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      document.title = `(local) 清掃ロボット管理システム`;
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        preventDuplicate
      >
        <StackSnackbarManager />
        <ErrorBoundary>
          <BrowserRouter>
            <Suspense fallback={<FullPageLoader />}>
              <Routes>
                {/* --------------- 認証不要 --------------- */}
                <Route element={<UnAuthGuard />}>
                  {Object.entries(APP_ROUTES.public).map(([key, route]) => {
                    const Component = route.component;
                    return (
                      <Route
                        key={key}
                        path={route.path}
                        element={<Component />}
                      />
                    );
                  })}
                </Route>
                {/* --------------- 認証必須 --------------- */}
                <Route element={<AuthGuard />}>
                  {/* --------------- システム管理者のみ --------------- */}
                  <Route
                    element={
                      <>
                        <RoleGuard allowedRoles={[AppUserRole.SystemAdmin]} />
                        <ClearCacheOnRouteChange />
                      </>
                    }
                  >
                    {Object.entries(APP_ROUTES.systemAdminOnly).map(
                      ([key, route]) => {
                        const Component = route.component;
                        return (
                          <Route
                            key={key}
                            path={route.path}
                            element={<Component />}
                          />
                        );
                      }
                    )}
                  </Route>
                  {/* --------------- テナント管理者のみ --------------- */}
                  <Route
                    element={
                      <>
                        <RoleGuard allowedRoles={[AppUserRole.TenantAdmin]} />
                        <ClearCacheOnRouteChange />
                      </>
                    }
                  >
                    {Object.entries(APP_ROUTES.tenantAdminOnly).map(
                      ([key, route]) => {
                        const Component = route.component;
                        return (
                          <Route
                            key={key}
                            path={route.path}
                            element={<Component />}
                          />
                        );
                      }
                    )}
                  </Route>
                  {/* --------------- ログインユーザーのみ --------------- */}
                  {Object.entries(APP_ROUTES.me).map(([key, route]) => {
                    const Component = route.component;
                    return (
                      <Route
                        key={key}
                        path={route.path}
                        element={<Component />}
                      />
                    );
                  })}
                  {/* --------------- 業務画面 --------------- */}
                  <Route
                    element={
                      <>
                        <WorkerGurad />
                        <ClearCacheOnRouteChange />
                      </>
                    }
                  >
                    <Route path="/" element={<Home />} />
                    <Route
                      path={pathToSelectionWork()}
                      element={<SelectionWork />}
                    />
                    <Route
                      path={pathToCleaningEvent()}
                      element={<CleaningRobotList />}
                    />
                    <Route
                      path={baseRobotDetailPath()}
                      element={<RobotDetailPage />}
                    />
                    <Route
                      path={baseCleaningLogPath()}
                      element={<CleaningLog />}
                    />
                    <Route path={pathToSchedule()} element={<ScheduleList />} />
                    <Route path={baseMapDetailPath()} element={<MapDetail />} />
                    <Route
                      path={baseMapCleaningLogPath()}
                      element={<MapCleaningLog />}
                    />
                    <Route
                      path={pathToBuildingList()}
                      element={<CleaningRobotList />}
                    />
                    <Route
                      path={pathToRobotStatus()}
                      element={<RobotListPage />}
                    />
                    <Route
                      path={pathToMapSchedule()}
                      element={<MapSchedule />}
                    />
                    <Route
                      path={baseCleaningSchedule()}
                      element={<CleaningSchedulePage />}
                    />
                  </Route>
                </Route>
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
            <ReactQueryDevtools
              initialIsOpen={false}
              toggleButtonProps={{ style: { fillOpacity: 0.1 } }}
            />
          </BrowserRouter>
        </ErrorBoundary>
      </SnackbarProvider>
    </ThemeProvider>
  );
}
